import tw from 'twin.macro';

// Phase → dot color, so the current state reads at a glance without parsing text.
const PHASE_COLORS = {
  idle: '#9CA3AF',
  holding: '#22C55E',
  hold: '#22C55E',
  broken: '#EF4444',
  failed: '#EF4444',
  complete: '#38BDF8',
  completed: '#38BDF8',
};

const formatSeconds = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return `${numeric.toFixed(1)}s`;
};

const formatNumber = value => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return '-';
  }
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2);
};

const formatFlag = value => (value ? 'yes' : 'no');

const Row = ({ label, value, valueStyle }) => (
  <div tw="flex items-baseline justify-between gap-3 leading-none">
    <span tw="text-[9px] uppercase tracking-[0.08em] text-white/45">{label}</span>
    <span tw="text-[11px] font-[700] text-white/90 tabular-nums" css={valueStyle}>
      {value}
    </span>
  </div>
);

// Compact always-on readout of the backend's hold telemetry, pinned to the
// bottom corner of the video like an in-game ping counter: small, monospaced
// numbers, semi-transparent, and deliberately non-interactive so it never eats a
// tap meant for the video underneath.
const HoldTelemetryOverlay = ({ telemetry, isStackedMobile = false }) => {
  const hold = telemetry?.hold;

  if (!hold || typeof hold !== 'object') {
    return null;
  }

  const completion = telemetry?.completion ?? {};
  const accuracy = telemetry?.accuracy ?? {};
  const diagnostics = telemetry?.diagnostics ?? {};
  const phase = typeof hold.phase === 'string' ? hold.phase : '-';
  const phaseColor = PHASE_COLORS[phase.toLowerCase()] ?? '#E5E7EB';
  const holdTimer = telemetry?.holdTimer;

  return (
    <div
      // z sits above the video/canvas layers and the positioning border, but
      // below the modals (9999) so a pause/settings sheet still covers it.
      tw="pointer-events-none fixed z-[9000] w-[168px] rounded-[10px] border border-white/15 bg-black/60 px-2.5 py-2 font-mono backdrop-blur-[3px] shadow-[0_6px_18px_rgba(0,0,0,0.35)]"
      // Stacked mobile puts the reference video in the lower half of the screen,
      // so the readout sits a little higher to stay off its bottom edge.
      css={isStackedMobile ? tw`bottom-24 right-3` : tw`bottom-4 right-4`}
    >
      <div tw="mb-1.5 flex items-center gap-1.5 border-b border-white/10 pb-1.5">
        <span
          tw="h-1.5 w-1.5 shrink-0 rounded-full"
          style={{ backgroundColor: phaseColor, boxShadow: `0 0 6px ${phaseColor}` }}
        />
        <span tw="text-[9px] font-[700] uppercase tracking-[0.14em] text-white/70">Hold</span>
        <span tw="ml-auto text-[10px] font-[700] uppercase text-white/90">{phase}</span>
      </div>

      <div tw="space-y-[3px]">
        <Row
          label="Elapsed"
          value={`${formatSeconds(hold.elapsed_s)} / ${formatSeconds(hold.duration_s)}`}
        />
        <Row
          label="Score"
          value={`${formatNumber(accuracy.score)} / ${formatNumber(
            accuracy.threshold ?? hold.score_threshold
          )}`}
          valueStyle={accuracy.met ? tw`text-[#22C55E]` : tw`text-white/90`}
        />
        <Row
          label="Frames"
          value={`${formatNumber(completion.current_frame)} / ${formatNumber(
            completion.required_frame
          )}`}
        />
        <Row
          label="Ckpt"
          value={`${formatNumber(completion.checkpoints_passed)} / ${formatNumber(
            completion.checkpoints_total
          )}`}
        />
        <Row label="Zone" value={formatNumber(hold.zone_frame)} />
        <Row label="Timer" value={formatNumber(holdTimer)} />
        <Row
          label="Started"
          value={`${formatFlag(hold.started)} / ${formatFlag(hold.complete)}`}
          valueStyle={hold.complete ? tw`text-[#38BDF8]` : tw`text-white/90`}
        />
        <Row label="Rep" value={formatFlag(telemetry?.rep_in_progress)} />
        {diagnostics.cost !== undefined && (
          <Row label="Cost" value={formatNumber(diagnostics.cost)} />
        )}
      </div>
    </div>
  );
};

export default HoldTelemetryOverlay;
